/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionGroup } from 'client-code/actions';
import { PreviewGroup, ScreenType } from 'client-code/previews';

import { APPLICATION_FEATURE_AREA } from '../../../config';
import { AppUtils } from '../../AppUtils';
import { Channel } from '../../entities/Channel';
import { LiveTvError } from '../../error';
import { ServicesClientImpl, VodInteractor } from '../../interactors';
import { PlaybackDataModelImpl } from '../../playback/impls';
import {
    TActionList,
    TActionsVodOfferParams,
    TChannelV2,
    TOfferPreviewCustomData,
    TPlayNextContentActionsParams,
    TPlayNextParams,
} from '../../types';
import { UniqueIdentifier } from '../../utils';
import { MetadataType } from '../enums';
import { IMetadata, IMetadataModel } from '../interfaces';

/* eslint-disable class-methods-use-this */
export class SocuMetadataModelImpl implements IMetadataModel {
    private static TAG: string = 'SocuMetadataModelImpl';

    private mMetadata: IMetadata = {};

    private mError: any = null;

    private mServices = new ServicesClientImpl();

    private mvodServices = new VodInteractor();

    private async fetchVodActions(channel: TChannelV2 | Channel): Promise<void> {
        const { offerId } = this.mMetadata.watchAction?.kernel || {};
        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallIdForLiveTv();
        const requestParams: TActionsVodOfferParams = {
            vodOfferId: offerId || '',
            channelNumber: channel.channelNumber || '',
            groups: [ActionGroup.ScheduleOnepass, ActionGroup.ScheduleBookmark],
            screenType: APPLICATION_FEATURE_AREA.PLAYER,
            actionsNetworkCallId: networkCallId,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .getVodOfferActionsV2(requestParams)
                .then((response: TActionList) => {
                    if (
                        response.actionsNetworkCallId ===
                        UniqueIdentifier.getActionsNetworkCallIdForLiveTv()
                    ) {
                        this.mMetadata.actionList = response.actionList;
                        resolve();
                    }
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async fetchVodOfferPreviewData(
        socuOfferId: string,
        previewData: object,
        screenType: ScreenType,
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            this.mServices
                .getCompleteVodOfferPreviewData(socuOfferId, previewData, screenType)
                .then((response: TOfferPreviewCustomData) => {
                    this.mMetadata.offerPreview = response;
                    resolve();
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async retrieveActionList(): Promise<void> {
        return new Promise((resolve, reject) => {
            // TODO: Update this once SocuDataModelImpl is removed and moved to DataLayer
            this.mMetadata.actionList = PlaybackDataModelImpl.getInstance().getActionList();
            if (AppUtils.isEmpty(this.mMetadata.actionList)) {
                reject();
            } else {
                resolve();
            }
        });
    }

    private async retrieveActionDetails(): Promise<void> {
        return new Promise((resolve, reject) => {
            // TODO: Update this once SocuDataModelImpl is removed and moved to DataLayer
            this.mMetadata.watchAction = PlaybackDataModelImpl.getInstance().getActionDetails();
            if (AppUtils.isEmpty(this.mMetadata.watchAction)) {
                reject();
            } else {
                resolve();
            }
        });
    }

    private async retrieveSocuFullPreview(): Promise<void> {
        const { offerId } = this.mMetadata.watchAction?.kernel || {};
        const { offerPreview } = this.mMetadata;

        if (!offerPreview) {
            return Promise.reject();
        }
        return this.fetchVodOfferPreviewData(offerId || '', offerPreview, ScreenType.FullBanner);
    }

    private async retrieveSocuPreview(): Promise<void> {
        const { offerId } = this.mMetadata.watchAction?.kernel || {};
        return this.fetchVodOfferPreviewData(offerId || '', {}, ScreenType.StandardBanner);
    }

    private async fetchPlayNextForSocu(): Promise<void> {
        try {
            const { contentId, collectionId, partnerId } = this.mMetadata.watchAction?.kernel || {};
            const playNextParams: TPlayNextParams = {
                contentId: contentId || '',
                unifiedCollectionId: collectionId || '',
                filter: 'watchlist',
                partnerId,
                sort: 'seasonOrYearAsc',
            };
            this.mMetadata.playNextEpisodes = await this.mServices.fetchPlayNext(playNextParams);
            return Promise.resolve();
        } catch (error) {
            this.mError = error;
            return Promise.reject(error);
        }
    }

    private async fetchPlayNextPreviewForSocu(): Promise<void> {
        try {
            const { contentId, partnerId } = this.mMetadata.watchAction?.kernel || {};
            this.mMetadata.playNextPreview = await this.mvodServices.fetchContentPreviewV3({
                contentId: contentId || '',
                partnerId: partnerId || '',
                groups: [PreviewGroup.Metadata, PreviewGroup.Linear],
            });

            return Promise.resolve();
        } catch (error) {
            this.mError = error;
            return Promise.reject(error);
        }
    }

    private async fetchPlayNextActionsForSocu(): Promise<void> {
        try {
            const { contentId, partnerId } = this.mMetadata.watchAction?.kernel || {};
            const playNextActionsContentActionParams: TPlayNextContentActionsParams = {
                contentId: contentId || '',
                screenType: APPLICATION_FEATURE_AREA.PLAYER,
                partnerId,
            };
            this.mMetadata.playNextActions = await this.mServices.getPlayNextContentActions(
                playNextActionsContentActionParams,
            );
            return Promise.resolve();
        } catch (error) {
            this.mError = error;
            return Promise.reject(error);
        }
    }

    public getMetadata(): IMetadata {
        return this.mMetadata;
    }

    public getError(): LiveTvError {
        return this.mError;
    }

    public prepareMetadataByType(playbackType: {
        metadataType:
            | MetadataType.ACTION_LIST
            | MetadataType.ACTION_DETAILS
            | MetadataType.PREVIEW
            | MetadataType.FULL_INFO_PREVIEW
            | MetadataType.VOD_ACTION_LIST
            | MetadataType.PLAY_NEXT_UNIFIED_COLLECTION
            | MetadataType.PLAY_NEXT_PREVIEW
            | MetadataType.PLAY_NEXT_ACTIONS;
        channel: TChannelV2 | Channel;
    }): Promise<void> {
        const { metadataType, channel } = playbackType;
        switch (metadataType) {
            case MetadataType.ACTION_LIST:
                return this.retrieveActionList();
            case MetadataType.ACTION_DETAILS:
                return this.retrieveActionDetails();
            case MetadataType.PREVIEW:
                return this.retrieveSocuPreview();
            case MetadataType.FULL_INFO_PREVIEW:
                return this.retrieveSocuFullPreview();
            case MetadataType.VOD_ACTION_LIST:
                return this.fetchVodActions(channel);
            case MetadataType.PLAY_NEXT_UNIFIED_COLLECTION:
                return this.fetchPlayNextForSocu();
            case MetadataType.PLAY_NEXT_PREVIEW:
                return this.fetchPlayNextPreviewForSocu();
            case MetadataType.PLAY_NEXT_ACTIONS:
                return this.fetchPlayNextActionsForSocu();
            default:
                return Promise.resolve();
        }
    }
}
